// Social Media Graphics Generator
// Generates platform-specific graphics for blog articles

export interface GraphicOptions {
  title: string;
  excerpt: string;
  author: string;
  date: string;
  image: string;
  platform: 'facebook' | 'twitter' | 'instagram' | 'linkedin';
}

export interface GraphicResult {
  success: boolean;
  imageUrl?: string;
  svg?: string;
  dimensions?: { width: number; height: number };
  error?: string;
}

/**
 * Generate a social media graphic for an article
 */
export async function generateSocialGraphic(options: GraphicOptions): Promise<GraphicResult> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/generate-social-graphics`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(options),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to generate graphic');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error generating social graphic:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}

/**
 * Generate graphics for all platforms
 */
export async function generateAllPlatformGraphics(
  article: {
    title: string;
    excerpt: string;
    author: string;
    date: string;
    image: string;
  }
): Promise<Record<string, GraphicResult>> {
  const platforms: Array<'facebook' | 'twitter' | 'instagram' | 'linkedin'> = [
    'facebook',
    'twitter',
    'instagram',
    'linkedin',
  ];

  const results: Record<string, GraphicResult> = {};

  await Promise.all(
    platforms.map(async (platform) => {
      const result = await generateSocialGraphic({
        ...article,
        platform,
      });
      results[platform] = result;
    })
  );

  return results;
}

/**
 * Download a graphic as an image file
 */
export function downloadGraphic(dataUrl: string, filename: string) {
  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Auto-post article to social media with generated graphics
 */
export async function autoPostToSocial(
  article: {
    title: string;
    excerpt: string;
    author: string;
    date: string;
    image: string;
    url: string;
  },
  platforms: string[] = ['facebook', 'twitter', 'instagram']
): Promise<any> {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/social-auto-post`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...article,
          platforms,
        }),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to post to social media');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error posting to social media:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
}
